import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Calendar,
  Check,
  ChevronLeft,
  AlertCircle,
  Users
} from 'lucide-react';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where
} from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';
import { useAuth } from '../../../context/AuthContext';
import { AppContext } from '../../../context/AppContext';
import { db } from '../../../firebase/config';
import { useCurrency } from '../../../utils/currencyUtils';
import { buildPayRunSummary } from '../../../utils/payrollUtils';

const PayRunForm = ({ isEditing = false }) => {
  const { payRunId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { activeBusiness } = useContext(AppContext);
  const { formatAmount } = useCurrency();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    startDate: '',
    endDate: '',
    paymentDate: '',
    notes: '',
    selectedEmployees: []
  });

  useEffect(() => {
    const fetchData = async () => {
      if (!currentUser) {
        setLoading(false);
        return;
      }

      if (!activeBusiness) {
        setErrors({ general: 'Select a business before creating or editing a pay run.' });
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        const payrollSnapshot = await getDocs(
          query(collection(db, 'employeePayrollDetails'), where('businessId', '==', activeBusiness.id))
        );

        const payrollRecords = payrollSnapshot.docs.map((snapshot) => ({
          payrollId: snapshot.id,
          ...snapshot.data()
        }));

        const employeeRecords = await Promise.all(
          payrollRecords.map(async (record) => {
            let employee = {
              id: record.employeeId,
              firstName: '',
              lastName: '',
              email: '',
              position: 'Worker'
            };

            if (record.employeeId) {
              const employeeSnapshot = await getDoc(doc(db, 'teamMembers', record.employeeId));
              if (employeeSnapshot.exists()) {
                const employeeData = employeeSnapshot.data();
                employee = {
                  id: employeeSnapshot.id,
                  firstName: employeeData.firstName || '',
                  lastName: employeeData.lastName || '',
                  email: employeeData.email || '',
                  position: employeeData.position || 'Worker'
                };
              }
            }

            return {
              ...record,
              employee,
              employmentType: record.employmentType || 'fullTime'
            };
          })
        );

        setEmployees(employeeRecords);

        if (isEditing && payRunId) {
          const payRunSnapshot = await getDoc(doc(db, 'payRuns', payRunId));
          if (!payRunSnapshot.exists()) {
            navigate('/payroll/runs');
            return;
          }

          const payRunData = payRunSnapshot.data();
          const toInputDate = (value) => {
            if (!value) return '';
            const date = value.toDate ? value.toDate() : new Date(value);
            return Number.isNaN(date.getTime()) ? '' : date.toISOString().split('T')[0];
          };

          setFormData({
            name: payRunData.name || '',
            startDate: toInputDate(payRunData.startDate),
            endDate: toInputDate(payRunData.endDate),
            paymentDate: toInputDate(payRunData.paymentDate),
            notes: payRunData.notes || '',
            selectedEmployees: Array.isArray(payRunData.employeeIds) ? payRunData.employeeIds : []
          });
        } else {
          const now = new Date();
          const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
          const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);

          setFormData((current) => ({
            ...current,
            name: `Payroll - ${now.toLocaleString('default', { month: 'long' })} ${now.getFullYear()}`,
            startDate: firstDay.toISOString().split('T')[0],
            endDate: lastDay.toISOString().split('T')[0],
            paymentDate: lastDay.toISOString().split('T')[0],
            selectedEmployees: employeeRecords.map((record) => record.employeeId)
          }));
        }
      } catch (error) {
        console.error('Error loading pay run form:', error);
        setErrors({ general: 'Failed to load payroll workers. Please try again.' });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [activeBusiness, currentUser, isEditing, navigate, payRunId]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
    setErrors((current) => ({ ...current, [name]: undefined, general: undefined }));
  };

  const toggleEmployee = (employeeId) => {
    setFormData((current) => ({
      ...current,
      selectedEmployees: current.selectedEmployees.includes(employeeId)
        ? current.selectedEmployees.filter((id) => id !== employeeId)
        : [...current.selectedEmployees, employeeId]
    }));
  };

  const toggleSelectAll = () => {
    setFormData((current) => ({
      ...current,
      selectedEmployees:
        current.selectedEmployees.length === employees.length
          ? []
          : employees.map((employee) => employee.employeeId)
    }));
  };

  const validateForm = () => {
    const nextErrors = {};

    if (!formData.name.trim()) nextErrors.name = 'Pay run name is required';
    if (!formData.startDate) nextErrors.startDate = 'Start date is required';
    if (!formData.endDate) nextErrors.endDate = 'End date is required';
    if (!formData.paymentDate) nextErrors.paymentDate = 'Payment date is required';
    if (formData.startDate && formData.endDate && new Date(formData.endDate) < new Date(formData.startDate)) {
      nextErrors.endDate = 'End date must be after start date';
    }
    if (formData.selectedEmployees.length === 0) nextErrors.selectedEmployees = 'Select at least one worker';

    return nextErrors;
  };

  const selectedPayrollRecords = employees.filter((employee) =>
    formData.selectedEmployees.includes(employee.employeeId)
  );

  const previewSummary = buildPayRunSummary(selectedPayrollRecords, {
    businessId: activeBusiness?.id,
    startDate: formData.startDate ? new Date(formData.startDate) : null,
    endDate: formData.endDate ? new Date(formData.endDate) : null,
    paymentDate: formData.paymentDate ? new Date(formData.paymentDate) : null,
    notes: formData.notes
  });

  const handleSubmit = async (event) => {
    event.preventDefault();

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      setSaving(true);

      const payRunData = {
        businessId: activeBusiness.id,
        name: formData.name.trim(),
        startDate: new Date(formData.startDate),
        endDate: new Date(formData.endDate),
        paymentDate: new Date(formData.paymentDate),
        notes: formData.notes.trim(),
        employeeIds: formData.selectedEmployees,
        employeeCount: previewSummary.employeeCount,
        totalAmount: previewSummary.totalAmount,
        totalGross: previewSummary.totalGross,
        totalDeductions: previewSummary.totalDeductions,
        totalEmployerContributions: previewSummary.totalEmployerContributions,
        status: 'draft',
        updatedAt: serverTimestamp()
      };

      if (isEditing && payRunId) {
        await updateDoc(doc(db, 'payRuns', payRunId), payRunData);
        navigate(`/payroll/runs/${payRunId}`);
      } else {
        const newPayRunId = uuidv4();
        await setDoc(doc(db, 'payRuns', newPayRunId), {
          ...payRunData,
          createdAt: serverTimestamp(),
          createdBy: currentUser.uid
        });
        navigate(`/payroll/runs/${newPayRunId}`);
      }
    } catch (error) {
      console.error('Error saving pay run:', error);
      setErrors({ general: 'Failed to save pay run. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-md p-6 flex justify-center items-center min-h-[300px]">
        <div className="animate-spin h-8 w-8 border-4 border-[#190192] rounded-full border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200">
        <button
          onClick={() => navigate('/payroll/runs')}
          className="text-[#190192] hover:text-[#2C0DB5] flex items-center"
        >
          <ChevronLeft size={16} className="mr-1" />
          Back to Pay Runs
        </button>
        <h1 className="mt-2 text-xl font-bold text-gray-900">
          {isEditing ? 'Edit Pay Run' : 'Create Pay Run'}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="p-6 space-y-6">
        {errors.general && (
          <div className="p-4 bg-red-50 rounded-lg text-red-700 flex items-center">
            <AlertCircle size={18} className="mr-2" />
            {errors.general}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Pay Run Name</label>
            <input
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className="w-full rounded-lg border border-gray-300 px-4 py-2.5 focus:border-[#190192] focus:ring-2 focus:ring-[#190192]/20"
            />
            {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Payment Date</label>
            <div className="relative">
              <Calendar size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="date"
                name="paymentDate"
                value={formData.paymentDate}
                onChange={handleInputChange}
                className="w-full rounded-lg border border-gray-300 pl-10 pr-4 py-2.5 focus:border-[#190192] focus:ring-2 focus:ring-[#190192]/20"
              />
            </div>
            {errors.paymentDate && <p className="mt-1 text-sm text-red-600">{errors.paymentDate}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
            <input
              type="date"
              name="startDate"
              value={formData.startDate}
              onChange={handleInputChange}
              className="w-full rounded-lg border border-gray-300 px-4 py-2.5 focus:border-[#190192] focus:ring-2 focus:ring-[#190192]/20"
            />
            {errors.startDate && <p className="mt-1 text-sm text-red-600">{errors.startDate}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
            <input
              type="date"
              name="endDate"
              value={formData.endDate}
              onChange={handleInputChange}
              className="w-full rounded-lg border border-gray-300 px-4 py-2.5 focus:border-[#190192] focus:ring-2 focus:ring-[#190192]/20"
            />
            {errors.endDate && <p className="mt-1 text-sm text-red-600">{errors.endDate}</p>}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
          <textarea
            name="notes"
            rows={3}
            value={formData.notes}
            onChange={handleInputChange}
            className="w-full rounded-lg border border-gray-300 px-4 py-2.5 focus:border-[#190192] focus:ring-2 focus:ring-[#190192]/20"
            placeholder="Optional notes for this payroll run"
          />
        </div>

        <div className="bg-gray-50 rounded-lg border border-gray-200 p-5">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-gray-500">Workers Selected</p>
              <p className="text-xl font-semibold text-gray-900">{previewSummary.employeeCount}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Estimated Gross</p>
              <p className="text-xl font-semibold text-gray-900">{formatAmount(previewSummary.totalGross)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Estimated Deductions</p>
              <p className="text-xl font-semibold text-gray-900">{formatAmount(previewSummary.totalDeductions)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Estimated Net Payroll</p>
              <p className="text-xl font-semibold text-[#190192]">{formatAmount(previewSummary.totalAmount)}</p>
            </div>
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-3">
            <label className="text-sm font-medium text-gray-700">Workers in this pay run</label>
            <button
              type="button"
              onClick={toggleSelectAll}
              className="text-sm text-[#190192] hover:text-[#2C0DB5]"
            >
              {formData.selectedEmployees.length === employees.length ? 'Deselect All' : 'Select All'}
            </button>
          </div>

          {errors.selectedEmployees && (
            <p className="mb-2 text-sm text-red-600">{errors.selectedEmployees}</p>
          )}

          {employees.length === 0 ? (
            <div className="bg-gray-50 rounded-lg border border-gray-200 p-6 text-center">
              <Users size={24} className="mx-auto text-gray-400 mb-2" />
              <p className="text-gray-600">No payroll workers found. Add payroll details for staff first.</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="divide-y divide-gray-200">
                {employees.map((employee) => {
                  const preview = buildPayRunSummary([employee], {
                    businessId: activeBusiness?.id,
                    startDate: formData.startDate ? new Date(formData.startDate) : null,
                    endDate: formData.endDate ? new Date(formData.endDate) : null,
                    paymentDate: formData.paymentDate ? new Date(formData.paymentDate) : null,
                    notes: formData.notes
                  }).previews[0];

                  return (
                    <label
                      key={employee.employeeId}
                      className={`flex items-center justify-between p-4 cursor-pointer ${
                        formData.selectedEmployees.includes(employee.employeeId) ? 'bg-purple-50' : 'hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.selectedEmployees.includes(employee.employeeId)}
                          onChange={() => toggleEmployee(employee.employeeId)}
                          className="h-4 w-4 text-[#190192] border-gray-300 rounded"
                        />
                        <div className="ml-3">
                          <p className="text-sm font-medium text-gray-900">
                            {employee.employee.firstName} {employee.employee.lastName}
                          </p>
                          <p className="text-sm text-gray-500">
                            {employee.employee.position || 'Worker'} - {employee.employmentType || 'fullTime'} - {employee.salaryType}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium text-gray-900">{formatAmount(preview?.netAmount || 0)}</p>
                        <p className="text-xs text-gray-500">Net estimate</p>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-4 border-t border-gray-200 pt-6">
          <button
            type="button"
            onClick={() => navigate('/payroll/runs')}
            className="px-6 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving || !activeBusiness}
            className={`px-6 py-2.5 rounded-lg text-white font-medium flex items-center ${
              saving || !activeBusiness ? 'bg-gray-400 cursor-not-allowed' : 'bg-[#190192] hover:bg-[#2C0DB5]'
            }`}
          >
            {saving ? (
              <>
                <span className="animate-spin h-4 w-4 mr-2 border-2 border-white rounded-full border-t-transparent"></span>
                Saving...
              </>
            ) : (
              <>
                <Check size={18} className="mr-2" />
                {isEditing ? 'Update Pay Run' : 'Create Pay Run'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default PayRunForm;
